import { config } from '../config/runtimeConfig.js';

// Адрес b2b-api читаем В МОМЕНТ ЗАПРОСА, не при загрузке модуля: в виджете
// бандл исполняется раньше, чем bootstrap кладёт `apiUrl` в mount-параметры, и
// снимок на старте был '' → запросы уходили на сайт дилера (/api/image-proxy
// на andys-motors.com → 404) и ни одно превью не резолвилось (#1469).
const apiBaseUrl = () => config.API_BASE_URL;

// В виджете apiUrl уже оканчивается на `/api` (`https://…/api`), на standalone —
// нет (`https://app.shiftgears.ai`): тот же приём, что у остальных вызовов в
// services/api.js — иначе выходило `/api/api/image-proxy` → 404.
const imageProxyEndpoint = (base) => `${base}${base.endsWith('/api') ? '' : '/api'}/image-proxy`;
const CAR_DEMO_BUCKET_NAME = 'b2b-car-demo';
const CAR_INVENTORY_BUCKET_SUBSTRING = 'inventory-images';

// Memory cache для URL'ов
const urlCache = new Map();

// Абсолютный URL фото из НАШЕГО inventory-бакета → { bucket, path } для
// image-proxy (#1065), иначе null. Имя бакета зависит от окружения
// (b2b-inventory-images-test, shiftgears-b2b-prod-inventory-images), поэтому
// узнаём его по подстроке; хост и бакет срезаются — прокси сам строит s3://.
const inventoryBucketPath = (url) => {
  const pathParts = new URL(url).pathname.split('/').filter((p) => p);
  return pathParts[0]?.includes(CAR_INVENTORY_BUCKET_SUBSTRING)
    ? { bucket: pathParts[0], path: pathParts.slice(1).join('/') }
    : null;
};

function getCacheKey(path, width, height, quality, format) {
  return `${path}_${width}x${height}_q${quality}_${format}`;
}

/**
 * Генерирует imgproxy URL для одного изображения через GET запрос
 */
export const generateImgproxyUrl = async (path, width, height, options = {}) => {
  const { quality = 80, format = 'webp' } = options;
  // #1065 - так как логика фоллбэка для с3/минио фото и автодис фото отличается,
  // введем флаг-переменную для выбора фоллбэка
  let autodisFlow = false;
  let imageBucket = CAR_DEMO_BUCKET_NAME;
  let pathToSend = path;

  // imgproxy-бэкенд принимает ТОЛЬКО относительные `/assets/...` ключи и
  // строит из них `s3://<bucket>/...` (см. image-proxy.service.ts: любой
  // другой path → 400 "must start with /assets/"), КРОМЕ ключей нашего
  // inventory-бакета (#1065, ниже). Поэтому прочие АБСОЛЮТНЫЕ URL отдаём
  // напрямую, без прокси:
  //   • фото с сайта дилера (andys-motors.com);
  //   • перезалитые в наше объектное хранилище фото Heiwa (#1048) — раньше
  //     уходили в прокси → 400 → null → "Image not available" на VDP, хотя
  //     на карточке (raw <img>) показывались нормально.
  // Ресайз им не нужен — это уже готовые, напрямую отдаваемые картинки.

  // #1065 - теперь ресайзим, если это фотки из нашего инвентори бакета
  // (inventoryBucketPath).
  if (path && /^https?:\/\//i.test(path)) {
    const inBucket = inventoryBucketPath(path);
    if (!inBucket) return path;
    imageBucket = inBucket.bucket;
    pathToSend = inBucket.path;
  } else autodisFlow = true;

  const cacheKey = getCacheKey(pathToSend, width, height, quality, format);

  // Проверяем memory cache
  if (urlCache.has(cacheKey)) {
    return urlCache.get(cacheKey);
  }

  try {
    // GET запрос - браузер автоматически кеширует благодаря Cache-Control headers
    const params = new URLSearchParams({
      path: pathToSend,
      bucketName: imageBucket,
      width: width.toString(),
      height: height.toString(),
      quality: quality.toString(),
      format,
    });

    const response = await fetch(`${imageProxyEndpoint(apiBaseUrl() || '')}?${params}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      if (autodisFlow) {
        console.error(
          `Failed to generate imgproxy URL for ${path}:`,
          response.statusText
        );
        return null;
      }
      return path;
    }

    const result = await response.json();
    const imgproxyUrl = result.imgproxyUrl;

    // Сохраняем в memory cache
    urlCache.set(cacheKey, imgproxyUrl);

    return imgproxyUrl;
  } catch (error) {
    if (autodisFlow) {
      console.error(`Error generating imgproxy URL for ${path}:`, error);
      return null;
    }
    return path;
  }
};

// Отдельный кеш для фото с сайта дилера (#1469): ключ — сам URL + размеры.
// Неудача тоже кешируется (null) на время сессии: превью не критично, а без
// этого каждый ремаунт ленты (Buy → Rent → Buy) заново слал те же запросы.
const remoteUrlCache = new Map();

// Высота коробки `fit` для фото из бакета: не ниже 4:3 от ширины. Плитка шире
// 4:3 (16:9 на main) иначе получила бы 4:3-фото уже себя (272×152 → 203×152)
// и растянула его; плитке 4:3 и выше это ничего не меняет.
const bucketFitHeight = (width, height) => Math.max(height, Math.round((width * 3) / 4));

const remoteCacheKey = (url, width, height, quality, format) =>
  `${url}_${width}x${height}_q${quality}_${format}`;

/**
 * Synchronous cache peek for `generateRemoteImgproxyUrl` (#1469) — lets a
 * remounting component start on the known answer instead of a placeholder.
 *
 * @returns {string|null|undefined} URL, null (known failure), undefined (unknown)
 */
export const peekRemoteImgproxyUrl = (url, width, height, options = {}) => {
  const { quality = 75, format = 'webp' } = options;
  return remoteUrlCache.get(remoteCacheKey(url, width, height, quality, format));
};

/**
 * Resized URL for a photo hosted on the DEALER's site or stored in OUR
 * inventory bucket (#1469) — e.g. Andys'
 * MODX `/assets/resourceimages/...`, 4032×3024 originals (~0.9MB each) that a
 * 136px card was downloading in full. The b2b-api signs an imgproxy URL that
 * fetches the https source itself; the origin must be on the backend allowlist
 * (`IMGPROXY_ALLOWED_REMOTE_ORIGINS`).
 *
 * Photos in OUR inventory bucket (Andys' uploads since the S3 switch, every
 * photo on test) go by path + bucket instead: the remote route only allows
 * dealer origins and answered them 400, so the tile loaded the full original
 * (7 of the first 10 prod tiles on 2026-10-16, 0.3–1.6MB each). That route
 * resizes with `fit`, not `fill`, so the box asked for is at least 4:3 tall
 * (bucketFitHeight): a 4:3 photo then comes back the full requested
 * width and `object-fit: cover` crops it instead of stretching it.
 *
 * Opt-in (not wired into `generateImgproxyUrl`, which the catalog uses): the
 * caller MUST fall back to the original on `null` and on an <img> load error —
 * the proxy can be down (2026-10: its TLS certificate had expired).
 *
 * @param {string} url - absolute https URL of the original photo
 * @param {number} width
 * @param {number} height
 * @param {{ quality?: number, format?: string }} [options]
 * @returns {Promise<string|null>} imgproxy URL, or null when it can't be had
 */
export const generateRemoteImgproxyUrl = async (url, width, height, options = {}) => {
  if (!url || !/^https:\/\//i.test(url)) return null;
  const { quality = 75, format = 'webp' } = options;
  const cacheKey = remoteCacheKey(url, width, height, quality, format);
  if (remoteUrlCache.has(cacheKey)) return remoteUrlCache.get(cacheKey);
  // Без адреса API запрос ушёл бы на страницу хоста — не шлём.
  const apiBase = apiBaseUrl();
  if (!apiBase) return null;

  let result = null;
  try {
    const inBucket = inventoryBucketPath(url);
    const params = new URLSearchParams({
      ...(inBucket ? { path: inBucket.path, bucketName: inBucket.bucket } : { url }),
      width: String(width),
      height: String(inBucket ? bucketFitHeight(width, height) : height),
      quality: String(quality),
      format,
    });
    const response = await fetch(`${imageProxyEndpoint(apiBase)}?${params}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (response.ok) {
      const { imgproxyUrl } = await response.json();
      result = imgproxyUrl || null;
    }
  } catch {
    result = null;
  }
  remoteUrlCache.set(cacheKey, result);
  return result;
};

